import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import { changeOwnPasswordApi } from "../api/client";
import { PASSWORD_POLICY_CHECKS, passwordPolicyMet } from "../utils/passwordPolicy";

/**
 * Após login com hash legado (cipher PHP): o usuário escolhe a senha nova.
 * Só então gravamos Argon2id da senha digitada — nunca do decode automático.
 */
export default function LegacyPasswordUpgradeDialog({ open, currentPassword, onComplete }) {
  const { t } = useTranslation("app");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const rules = useMemo(
    () => [
      { id: "len", label: t("platformPage.legacyPasswordRuleLen") },
      { id: "upper", label: t("platformPage.legacyPasswordRuleUpper") },
      { id: "num", label: t("platformPage.legacyPasswordRuleNum") },
      { id: "sym", label: t("platformPage.legacyPasswordRuleSym") },
    ],
    [t]
  );

  const mismatch = Boolean(confirm) && confirm !== newPassword;
  const canSubmit =
    Boolean(currentPassword) && passwordPolicyMet(newPassword) && newPassword === confirm && !busy;

  const handleSubmit = async () => {
    setError("");
    if (!passwordPolicyMet(newPassword)) {
      setError(t("platformPage.legacyPasswordUpgradePolicy"));
      return;
    }
    if (newPassword !== confirm) {
      setError(t("platformPage.legacyPasswordUpgradeMismatch"));
      return;
    }
    setBusy(true);
    try {
      await changeOwnPasswordApi(currentPassword, newPassword, { skipCompanyHeader: true });
      onComplete?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("platformPage.legacyPasswordUpgradeFailed");
      setError(typeof msg === "string" ? msg : t("platformPage.legacyPasswordUpgradeFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" disableEscapeKeyDown onClose={() => {}}>
      <DialogTitle>{t("platformPage.legacyPasswordUpgradeTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("platformPage.legacyPasswordUpgradeBody")}
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <TextField
          label={t("platformPage.legacyPasswordUpgradeNew")}
          type="password"
          fullWidth
          margin="dense"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
        />
        <TextField
          label={t("platformPage.legacyPasswordUpgradeConfirm")}
          type="password"
          fullWidth
          margin="dense"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch}
          helperText={mismatch ? t("platformPage.legacyPasswordUpgradeMismatch") : " "}
          autoComplete="new-password"
        />
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {PASSWORD_POLICY_CHECKS.map((c) => {
            const ok = c.test(newPassword);
            const rule = rules.find((r) => r.id === c.id);
            return (
              <Typography key={c.id} variant="caption" color={ok ? "success.main" : "text.secondary"}>
                {ok ? "✓" : "○"} {rule?.label || c.id}
              </Typography>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {busy ? t("loading") : t("platformPage.legacyPasswordUpgradeSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
